/**
 * Client HTTP commun vers le backend NestJS.
 *
 * - Ajoute le jeton d'accès (`Authorization: Bearer …`).
 * - Déballe l'enveloppe du backend : succès `{ success, data, message }`,
 *   erreur `{ success, statusCode, message, errors }`.
 * - Sur une réponse 401, renouvelle les jetons une seule fois (`/auth/refresh`)
 *   puis rejoue la requête ; si le renouvellement échoue, la session est close.
 * - Anti double-clic : une même action (même adresse, même contenu) n'est pas renvoyée
 *   tant qu'elle est en cours, ni dans les 10 secondes qui suivent sa réussite.
 * - Hors ligne : chaque lecture réussie est gardée sur l'appareil ; serveur injoignable, la
 *   dernière version gardée est rendue (l'en-tête indique sa date).
 *
 * Jetons : l'accès reste en mémoire ; le renouvellement est gardé dans
 * `localStorage` (« Se souvenir de moi ») ou `sessionStorage` (onglet courant).
 */
import { API_BASE_URL } from "./api";
import { idb } from "@/offline/idb";
import { getCacheOwner, touchOnline } from "@/offline/session";
import { isForcedOffline, markOffline, markOnline } from "@/offline/status";

/** Origine du backend (pour les fichiers servis hors de /api/v1, ex. /uploads/…). */
export const API_ORIGIN = new URL(API_BASE_URL, globalThis.location?.origin).origin;

/** URL affichable d'un fichier du backend (chemin relatif « /uploads/… » ou URL complète). */
export function assetUrl(path: string | null | undefined): string {
  if (!path) return "";
  return path.startsWith("/") ? `${API_ORIGIN}${path}` : path;
}

/**
 * Prépare une image pour l'enregistrement : une image choisie sur le poste (data URL) est d'abord envoyée
 * au backend ; une image déjà servie par le backend redevient un chemin relatif. Vide => undefined.
 */
export async function storeImage(value: string | null | undefined): Promise<string | undefined> {
  if (!value) return undefined;
  if (value.startsWith("data:")) {
    const blob = await (await fetch(value)).blob();
    const ext = blob.type.split("/")[1] ?? "png";
    return http.uploadImage(blob, `image.${ext}`);
  }
  if (value.startsWith(`${API_ORIGIN}/`)) return value.slice(API_ORIGIN.length);
  return value;
}

/** Chaîne vide -> undefined (le backend refuse les champs optionnels vides, ex. téléphone). */
export function orUndefined(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

export const OFFLINE_NO_DATA =
  "Hors ligne : ces données n'ont pas encore été gardées sur cet appareil. Ouvrez cette page une fois avec la connexion.";
export const OFFLINE_ACTION = "Hors ligne : cette action demande une connexion. Réessayez quand le réseau revient.";

/** Erreur renvoyée par l'API (ou réseau indisponible : `status` = 0). */
export class ApiError extends Error {
  readonly status: number;
  readonly errors?: unknown;

  constructor(message: string, status: number, errors?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.errors = errors;
  }
}

/* ------------------------------------------------------------------------- */
/* Jetons                                                                     */
/* ------------------------------------------------------------------------- */

const REFRESH_KEY = "waitechedu.refresh-token";

let accessToken: string | null = null;
let sessionExpiredHandler: (() => void) | null = null;

function storageFor(remember: boolean): Storage | null {
  try {
    return remember ? localStorage : sessionStorage;
  } catch {
    return null;
  }
}

function readRefreshToken(): { token: string; remember: boolean } | null {
  try {
    const persistent = localStorage.getItem(REFRESH_KEY);
    if (persistent) return { token: persistent, remember: true };
    const session = sessionStorage.getItem(REFRESH_KEY);
    if (session) return { token: session, remember: false };
  } catch {
    /* stockage indisponible */
  }
  return null;
}

export const tokenStore = {
  save(access: string, refresh: string, remember: boolean): void {
    accessToken = access;
    tokenStore.clearRefresh();
    try {
      storageFor(remember)?.setItem(REFRESH_KEY, refresh);
    } catch {
      /* stockage indisponible : la session durera le temps du jeton d'accès */
    }
  },
  clearRefresh(): void {
    try {
      localStorage.removeItem(REFRESH_KEY);
      sessionStorage.removeItem(REFRESH_KEY);
    } catch {
      /* stockage indisponible */
    }
  },
  clear(): void {
    accessToken = null;
    tokenStore.clearRefresh();
  },
  hasRefreshToken(): boolean {
    return readRefreshToken() !== null;
  },
};

/** Fonction appelée quand la session ne peut plus être renouvelée (retour à la connexion). */
export function onSessionExpired(handler: (() => void) | null): void {
  sessionExpiredHandler = handler;
}

/* ------------------------------------------------------------------------- */
/* Renouvellement                                                             */
/* ------------------------------------------------------------------------- */

interface TokenPair {
  accessToken: string;
  refreshToken: string;
}

/** Issue d'un renouvellement : réussi, serveur injoignable (jetons gardés) ou session révoquée. */
export type RefreshResult = "ok" | "offline" | "invalid";

let refreshing: Promise<RefreshResult> | null = null;

/** Renouvelle les jetons (une seule requête même si plusieurs appels échouent en même temps). */
export function refreshSession(): Promise<RefreshResult> {
  if (refreshing) return refreshing;
  const stored = readRefreshToken();
  if (!stored) return Promise.resolve("invalid");

  refreshing = request<TokenPair>("/auth/refresh", {
    method: "POST",
    body: { refreshToken: stored.token },
    auth: false,
  })
    .then((pair): RefreshResult => {
      tokenStore.save(pair.accessToken, pair.refreshToken, stored.remember);
      return "ok";
    })
    .catch((error: unknown): RefreshResult => {
      // Sans réseau, la session n'est pas perdue : elle sera renouvelée au retour de la connexion.
      if (isOfflineError(error)) return "offline";
      tokenStore.clear();
      return "invalid";
    })
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
}

export async function refreshTokens(): Promise<boolean> {
  return (await refreshSession()) === "ok";
}

/* ------------------------------------------------------------------------- */
/* Requêtes                                                                   */
/* ------------------------------------------------------------------------- */

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  /** Paramètres de requête (les valeurs vides sont ignorées). */
  query?: Record<string, string | number | boolean | undefined | null>;
  /** `false` : pas de jeton ni de renouvellement (connexion, renouvellement). */
  auth?: boolean;
  /** Clé d'envoi : le serveur n'enregistre l'action qu'une fois, même renvoyée (file hors ligne). */
  idempotencyKey?: string;
  /** Renvoi d'une action de la file hors ligne : pas d'anti double-clic (la clé d'envoi protège déjà). */
  replay?: boolean;
}

function buildUrl(path: string, query?: RequestOptions["query"]): string {
  const url = `${API_BASE_URL}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") params.set(key, String(value));
  });
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

async function send(path: string, options: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = { Accept: "application/json" };
  const isForm = options.body instanceof FormData;
  if (options.body !== undefined && !isForm) headers["Content-Type"] = "application/json";
  if (options.auth !== false && accessToken) headers.Authorization = `Bearer ${accessToken}`;
  if (options.idempotencyKey) headers["Idempotency-Key"] = options.idempotencyKey;
  // Mode hors ligne choisi : rien ne part, tout se lit et s'enregistre sur l'appareil.
  if (isForcedOffline()) throw new ApiError("Mode hors ligne : repassez en ligne pour cette action.", 0);
  let res: Response;
  try {
    res = await fetch(buildUrl(path, options.query), {
      method: options.method ?? "GET",
      headers,
      body: isForm ? (options.body as FormData) : options.body !== undefined ? JSON.stringify(options.body) : undefined,
    });
  } catch {
    throw new ApiError("Impossible de joindre le serveur. Vérifiez votre connexion ou réessayez plus tard.", 0);
  }
  // Serveur en panne derrière Nginx : traité comme une coupure (les données de l'appareil prennent le relais).
  if (res.status === 502 || res.status === 503 || res.status === 504) {
    throw new ApiError("Le serveur ne répond pas pour le moment. Réessayez dans quelques instants.", 0);
  }
  return res;
}

interface Envelope {
  success?: boolean;
  data?: unknown;
  message?: string | string[];
  errors?: unknown;
  meta?: { hasNextPage?: boolean } | null;
}

async function parseEnvelope(res: Response): Promise<Envelope> {
  const text = await res.text();
  let body: Envelope | null = null;
  if (text) {
    try {
      body = JSON.parse(text) as Envelope;
    } catch {
      body = null;
    }
  }
  if (!res.ok || body?.success === false) {
    const raw = body?.message;
    const details = Array.isArray(body?.errors)
      ? (body.errors as Array<{ message?: string }>).map((e) => e.message).filter(Boolean).join(" · ")
      : "";
    const message = details || (Array.isArray(raw) ? raw.join(" ") : raw) || `Erreur ${res.status}.`;
    throw new ApiError(message, res.status, body?.errors);
  }
  if (body && typeof body === "object" && "success" in body) return body;
  return { data: body };
}

/** Serveur injoignable (réseau coupé ou serveur en panne). */
export function isOfflineError(error: unknown): error is ApiError {
  return error instanceof ApiError && error.status === 0;
}

interface CachedEnvelope {
  envelope: Envelope;
  savedAt: number;
}

/** Clé de mise en mémoire d'une lecture (null : écriture, authentification ou aucun utilisateur). */
function cacheKeyFor(path: string, options: RequestOptions): string | null {
  const owner = getCacheOwner();
  if (!owner || (options.method ?? "GET") !== "GET" || options.auth === false || path.startsWith("/auth/")) return null;
  return `${owner}|${buildUrl(path, options.query)}`;
}

/** Données d'une lecture gardées sur l'appareil (null si jamais lue avec la connexion). */
export async function readCached<T>(path: string, query?: RequestOptions["query"]): Promise<T | null> {
  const key = cacheKeyFor(path, { query });
  const hit = key ? await idb.get<CachedEnvelope>("cache", key) : undefined;
  return hit ? (hit.envelope.data as T) : null;
}

/** Remplace les données gardées d'une lecture (ex. notes saisies hors ligne, visibles à la réouverture). */
export async function writeCached(path: string, query: RequestOptions["query"], data: unknown): Promise<void> {
  const key = cacheKeyFor(path, { query });
  if (!key) return;
  const hit = await idb.get<CachedEnvelope>("cache", key);
  await idb.set("cache", key, { envelope: { ...hit?.envelope, data }, savedAt: hit?.savedAt ?? Date.now() });
}

/** Requêtes en cours (pour attendre qu'une page ait fini de charger : préparation du mode hors ligne). */
let inFlight = 0;
let lastActivity = 0;

/** Attend que plus aucune requête ne soit en cours depuis `quietMs` (au plus `maxMs`). */
export async function waitForNetworkIdle(quietMs = 700, maxMs = 25_000): Promise<void> {
  const start = Date.now();
  while (Date.now() - start < maxMs) {
    if (inFlight === 0 && Date.now() - lastActivity >= quietMs) return;
    await new Promise((resolve) => setTimeout(resolve, 150));
  }
}

async function call(path: string, options: RequestOptions = {}): Promise<Envelope> {
  inFlight += 1;
  lastActivity = Date.now();
  try {
    return await callOnce(path, options);
  } finally {
    inFlight -= 1;
    lastActivity = Date.now();
  }
}

async function callOnce(path: string, options: RequestOptions): Promise<Envelope> {
  const cacheKey = cacheKeyFor(path, options);
  let res: Response;
  try {
    res = await send(path, options);
    if (res.status === 401 && options.auth !== false) {
      const refreshed = await refreshSession();
      if (refreshed === "ok") res = await send(path, options);
      else if (refreshed === "offline") throw new ApiError(OFFLINE_ACTION, 0);
      else sessionExpiredHandler?.();
    }
  } catch (error) {
    if (!isOfflineError(error)) throw error;
    markOffline();
    if (!cacheKey) throw (options.method ?? "GET") === "GET" || isForcedOffline() ? error : new ApiError(OFFLINE_ACTION, 0);
    const hit = await idb.get<CachedEnvelope>("cache", cacheKey);
    if (!hit) throw new ApiError(OFFLINE_NO_DATA, 0);
    markOffline(hit.savedAt);
    return hit.envelope;
  }
  markOnline();
  touchOnline();
  const envelope = await parseEnvelope(res);
  if (cacheKey) void idb.set("cache", cacheKey, { envelope, savedAt: Date.now() } satisfies CachedEnvelope);
  return envelope;
}

/** Anti double-clic : délai pendant lequel une même action réussie n'est pas renvoyée (le serveur bloque aussi). */
const DUPLICATE_WINDOW_MS = 10_000;
const DUPLICATE_MESSAGE = "Cette action vient déjà d'être envoyée. Patientez quelques secondes avant de la refaire.";
/** Action en cours (Infinity) ou fin du blocage après sa réussite. */
const recentActions = new Map<string, number>();

/** Clé d'une action qui modifie des données ; null pour les lectures, l'authentification et les fichiers. */
function actionKey(path: string, options: RequestOptions): string | null {
  const method = options.method ?? "GET";
  // Renvoi de la file hors ligne : sa clé d'envoi le protège déjà contre tout double enregistrement.
  if (method === "GET" || path.startsWith("/auth/") || options.body instanceof FormData || options.replay) return null;
  return `${method} ${path} ${JSON.stringify(options.query ?? null)} ${JSON.stringify(options.body ?? null)}`;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const key = actionKey(path, options);
  if (!key) return (await call(path, options)).data as T;

  const now = Date.now();
  if ((recentActions.get(key) ?? 0) > now) throw new ApiError(DUPLICATE_MESSAGE, 409);
  for (const [k, until] of recentActions) if (until <= now) recentActions.delete(k);
  recentActions.set(key, Infinity);
  try {
    const data = (await call(path, options)).data as T;
    recentActions.set(key, Date.now() + DUPLICATE_WINDOW_MS);
    return data;
  } catch (error) {
    // Échec (réseau, refus) : l'action peut être refaite tout de suite.
    recentActions.delete(key);
    throw error;
  }
}

/** Récupère toutes les pages d'une liste paginée du backend (100 éléments par page). */
async function getAll<T>(path: string, query: RequestOptions["query"] = {}): Promise<T[]> {
  const items: T[] = [];
  for (let page = 1; ; page += 1) {
    const envelope = await call(path, { query: { ...query, page, limit: 100 } });
    items.push(...((envelope.data as T[] | undefined) ?? []));
    if (!envelope.meta?.hasNextPage) return items;
  }
}

export const http = {
  get: <T>(path: string, query?: RequestOptions["query"]) => request<T>(path, { query }),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, "method" | "body">) =>
    request<T>(path, { ...options, method: "POST", body }),
  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, "method" | "body">) =>
    request<T>(path, { ...options, method: "PUT", body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, "method" | "body">) =>
    request<T>(path, { ...options, method: "PATCH", body }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
  getAll,
  /** Envoie une image au backend et retourne son chemin public (« /uploads/… »). */
  uploadImage: async (file: Blob, filename = "image"): Promise<string> => {
    const form = new FormData();
    form.append("file", file, filename);
    return (await request<{ url: string }>("/uploads/images", { method: "POST", body: form })).url;
  },
};
